import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  EmptyState,
  EmptyStateBody,
  Grid,
  GridItem,
  Spinner,
  TextInput,
  Title,
} from '@patternfly/react-core';
import PageHeader from '@patternfly/react-component-groups/dist/dynamic/PageHeader';
import { SyncIcon } from '@patternfly/react-icons';
import type { AcquisitionOverviewResponse } from 'src/contracts/reporting';
import { parseAcquisitionOverviewResponse } from 'src/contracts/reporting';
import { useAuthedFetch } from 'src/hooks/useAuthedFetch';
import './reporting.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const DEFAULT_REPORTING_WINDOW_DAYS = 30;

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '—';
  }
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return '—';
  }
  return parsedDate.toLocaleString();
};

const formatRate = (value?: number | null) => {
  if (typeof value !== 'number') {
    return '—';
  }
  const percentageValue = value * 100;
  return `${percentageValue.toFixed(1)}%`;
};

const formatCampaignValue = (value?: string | null, fallbackLabel = '—') => {
  if (!value) {
    return fallbackLabel;
  }
  return value;
};

const toIsoIfSet = (value: string) => {
  if (!value.trim()) {
    return undefined;
  }
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return undefined;
  }
  return parsedDate.toISOString();
};

const toDateTimeLocalValue = (value: Date) => {
  const offsetMs = value.getTimezoneOffset() * 60_000;
  const localValue = new Date(value.getTime() - offsetMs);
  const isoValue = localValue.toISOString();
  return isoValue.slice(0, 16);
};

const defaultToValue = () => {
  const currentDate = new Date();
  const currentValue = toDateTimeLocalValue(currentDate);
  return currentValue;
};

const defaultFromValue = () => {
  const currentDate = new Date();
  const fromDate = new Date(
    currentDate.getTime() - DEFAULT_REPORTING_WINDOW_DAYS * 24 * 60 * 60 * 1000,
  );
  const fromValue = toDateTimeLocalValue(fromDate);
  return fromValue;
};

const buildOverviewParams = (fromValue: string, toValue: string) => {
  const params = new URLSearchParams();
  const fromIso = toIsoIfSet(fromValue);
  const toIso = toIsoIfSet(toValue);
  if (fromIso) {
    params.set('from', fromIso);
  }
  if (toIso) {
    params.set('to', toIso);
  }
  params.set('challenge_limit', '50');
  return params;
};

type MetricCardProps = {
  label: string;
  value: string | number;
};

const MetricCard = ({ label, value }: MetricCardProps) => (
  <Card className="reporting-summary-card">
    <CardTitle>{label}</CardTitle>
    <CardBody>
      <div className="reporting-summary-value">{value}</div>
    </CardBody>
  </Card>
);

const AcquisitionReportingDashboard = () => {
  const authedFetch = useAuthedFetch();
  const didLoadInitialReport = useRef(false);
  const [fromValue, setFromValue] = useState(() => defaultFromValue());
  const [toValue, setToValue] = useState(() => defaultToValue());
  const [overview, setOverview] = useState<AcquisitionOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOverview = useCallback(
    async (nextFromValue: string, nextToValue: string) => {
      setLoading(true);
      try {
        const params = buildOverviewParams(nextFromValue, nextToValue);
        const requestUrl = `${API_BASE}/api/reporting/acquisition/overview?${params.toString()}`;
        const response = await authedFetch(requestUrl);
        if (!response.ok) {
          throw new Error(`Acquisition report load failed (${response.status})`);
        }
        const raw = await response.json();
        const parsed = parseAcquisitionOverviewResponse(raw);
        if (!parsed.ok) {
          throw new Error(parsed.err.message);
        }
        setOverview(parsed.value);
        setError(null);
      } catch (err) {
        setError((err as Error).message);
        setOverview(null);
      } finally {
        setLoading(false);
      }
    },
    [authedFetch],
  );

  useEffect(() => {
    if (didLoadInitialReport.current) {
      return;
    }
    didLoadInitialReport.current = true;
    void fetchOverview(fromValue, toValue);
  }, [fetchOverview, fromValue, toValue]);

  const applyFilters = useCallback(async () => {
    await fetchOverview(fromValue, toValue);
  }, [fetchOverview, fromValue, toValue]);

  const clearFilters = useCallback(async () => {
    const nextFromValue = defaultFromValue();
    const nextToValue = defaultToValue();
    setFromValue(nextFromValue);
    setToValue(nextToValue);
    await fetchOverview(nextFromValue, nextToValue);
  }, [fetchOverview]);

  const loadedOverview = overview;
  const summary = loadedOverview?.summary ?? null;

  return (
    <div className="reporting-page">
      <PageHeader
        title="Acquisition Reporting"
        subtitle="Canonical challenge, landing, and campaign funnel performance"
      />

      <Card className="reporting-filter-card">
        <CardTitle>Filters</CardTitle>
        <CardBody>
          <Grid hasGutter>
            <GridItem sm={12} md={6} lg={4}>
              <TextInput
                type="datetime-local"
                value={fromValue}
                onChange={(_, value) => setFromValue(value)}
                aria-label="From"
              />
            </GridItem>
            <GridItem sm={12} md={6} lg={4}>
              <TextInput
                type="datetime-local"
                value={toValue}
                onChange={(_, value) => setToValue(value)}
                aria-label="To"
              />
            </GridItem>
            <GridItem sm={12} lg={4}>
              <div className="reporting-filter-actions">
                <Button
                  variant="primary"
                  icon={<SyncIcon />}
                  isLoading={loading}
                  onClick={() => {
                    void applyFilters();
                  }}
                >
                  Refresh
                </Button>{' '}
                <Button
                  variant="link"
                  onClick={() => {
                    void clearFilters();
                  }}
                >
                  Last 30 days
                </Button>
              </div>
            </GridItem>
          </Grid>
        </CardBody>
      </Card>

      <Card className="reporting-note-card">
        <CardTitle>Interpretation</CardTitle>
        <CardBody>
          <p>
            Funnel counts are landing-touch cohorts for the selected window. A touch stays counted
            as converted when it later reaches auth, queue, session, or attempt stages.
          </p>
          <p>
            Challenge preparation is canonical today. Share initiated count only increases when a
            durable backend share-initiation event is written for that challenge.
          </p>
        </CardBody>
      </Card>

      {error && <div className="reporting-error">{error}</div>}

      {loading && (
        <div className="reporting-loading">
          <Spinner size="xl" />
        </div>
      )}

      {!loading && !overview && !error && (
        <Card className="reporting-table-card">
          <CardBody>
            <EmptyState>
              <Title headingLevel="h4">No acquisition reporting loaded</Title>
              <EmptyStateBody>Refresh the report to load canonical acquisition metrics.</EmptyStateBody>
            </EmptyState>
          </CardBody>
        </Card>
      )}

      {!loading && summary && loadedOverview && (
        <>
          <Grid hasGutter className="reporting-summary-grid">
            <GridItem sm={12} md={6} xl={3}>
              <MetricCard label="Challenges Prepared" value={summary.challenges_prepared} />
            </GridItem>
            <GridItem sm={12} md={6} xl={3}>
              <MetricCard label="Shares Initiated" value={summary.share_initiated_count} />
            </GridItem>
            <GridItem sm={12} md={6} xl={3}>
              <MetricCard label="Landing Touches" value={summary.landing_touches} />
            </GridItem>
            <GridItem sm={12} md={6} xl={3}>
              <MetricCard label="Unique Visitors" value={summary.unique_visitors} />
            </GridItem>
            <GridItem sm={12} md={6} xl={3}>
              <MetricCard label="Auth Claims" value={summary.auth_claims} />
            </GridItem>
            <GridItem sm={12} md={6} xl={3}>
              <MetricCard label="Queue Joins" value={summary.queue_claims} />
            </GridItem>
            <GridItem sm={12} md={6} xl={3}>
              <MetricCard label="Attempt Starts" value={summary.attempt_claims} />
            </GridItem>
            <GridItem sm={12} md={6} xl={3}>
              <MetricCard label="Landing to Attempt" value={formatRate(summary.landing_to_attempt_rate)} />
            </GridItem>
          </Grid>

          <Card className="reporting-table-card">
            <CardTitle>Campaign Performance</CardTitle>
            <CardBody>
              <div className="reporting-muted">
                Window: {formatDateTime(summary.window_start)} to {formatDateTime(summary.window_end)}
              </div>
              {loadedOverview.campaigns.length === 0 ? (
                <EmptyState>
                  <Title headingLevel="h4">No campaign rows</Title>
                  <EmptyStateBody>No landing touches matched the selected window.</EmptyStateBody>
                </EmptyState>
              ) : (
                <div className="reporting-table-wrap">
                  <table className="reporting-table">
                    <thead>
                      <tr>
                        <th>Source</th>
                        <th>Medium</th>
                        <th>Campaign</th>
                        <th>UTM ID</th>
                        <th>Landings</th>
                        <th>Auth</th>
                        <th>Queue</th>
                        <th>Attempts</th>
                        <th>Landing to Attempt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadedOverview.campaigns.map((row) => (
                        <tr
                          key={`${row.utm_source || 'direct'}:${row.utm_medium || 'none'}:${row.utm_campaign || 'none'}:${row.utm_id || 'none'}`}
                        >
                          <td>{formatCampaignValue(row.utm_source, '(direct)')}</td>
                          <td>{formatCampaignValue(row.utm_medium)}</td>
                          <td>{formatCampaignValue(row.utm_campaign)}</td>
                          <td>{formatCampaignValue(row.utm_id)}</td>
                          <td>{row.landing_touches}</td>
                          <td>{row.auth_claims}</td>
                          <td>{row.queue_claims}</td>
                          <td>{row.attempt_claims}</td>
                          <td>{formatRate(row.landing_to_attempt_rate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>

          <Card className="reporting-table-card">
            <CardTitle>Challenge Performance</CardTitle>
            <CardBody>
              {loadedOverview.challenges.length === 0 ? (
                <EmptyState>
                  <Title headingLevel="h4">No challenge rows</Title>
                  <EmptyStateBody>
                    No prepared or landed challenge rows matched the selected window.
                  </EmptyStateBody>
                </EmptyState>
              ) : (
                <div className="reporting-table-wrap">
                  <table className="reporting-table">
                    <thead>
                      <tr>
                        <th>Challenge</th>
                        <th>Prepared At</th>
                        <th>Shares Initiated</th>
                        <th>Landings</th>
                        <th>Auth</th>
                        <th>Queue</th>
                        <th>Attempts</th>
                        <th>Landing to Attempt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadedOverview.challenges.map((row) => (
                        <tr key={row.challenge_id}>
                          <td>
                            <a href={row.challenge_url} target="_blank" rel="noreferrer">
                              {row.public_id}
                            </a>
                            <div className="reporting-muted">
                              {formatCampaignValue(row.robot_name)} · {formatCampaignValue(row.game_type_key)}
                            </div>
                          </td>
                          <td>{formatDateTime(row.created_at)}</td>
                          <td>{row.share_initiated_count}</td>
                          <td>{row.landing_touches}</td>
                          <td>{row.auth_claims}</td>
                          <td>{row.queue_claims}</td>
                          <td>{row.attempt_claims}</td>
                          <td>{formatRate(row.landing_to_attempt_rate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

export default AcquisitionReportingDashboard;
